import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { collection, query, getDocs, doc, updateDoc, deleteDoc, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Link, useNavigate } from 'react-router-dom';
import Spinner from '../components/Spinner';
import { Helmet } from 'react-helmet-async';
import './AdminDashboard.css';

const AdminDashboard = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [ads, setAds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('pending'); // 'pending', 'approved', 'all'

    useEffect(() => {
        // Simple security check (real app would have server-side rules)
        if (!user || user.role !== 'admin') {
            navigate('/');
            return;
        }

        fetchAds();
    }, [user, navigate]);

    const fetchAds = async () => {
        setLoading(true);
        try {
            const adsRef = collection(db, "ads");
            let q = query(adsRef);

            if (filter === 'pending') {
                q = query(adsRef, where("status", "==", "pending"));
            } else if (filter === 'approved') {
                q = query(adsRef, where("status", "==", "approved"));
            }

            const querySnapshot = await getDocs(q);
            const fetchedAds = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // Sort by createdAt descending
            fetchedAds.sort((a, b) => {
                const dateA = a.createdAt?.seconds || 0;
                const dateB = b.createdAt?.seconds || 0;
                return dateB - dateA;
            });

            setAds(fetchedAds);
        } catch (error) {
            console.error("Error fetching ads:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user && user.role === 'admin') {
            fetchAds();
        }
    }, [filter]);

    const handleApprove = async (id) => {
        try {
            await updateDoc(doc(db, "ads", id), { status: 'approved' });
            setAds(ads.filter(ad => ad.id !== id));
            alert("Ad approved successfully!");
        } catch (error) {
            console.error("Error approving ad:", error);
            alert("Failed to approve ad.");
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this ad? This action cannot be undone.")) {
            try {
                await deleteDoc(doc(db, "ads", id));
                setAds(ads.filter(ad => ad.id !== id));
                alert("Ad deleted successfully!");
            } catch (error) {
                console.error("Error deleting ad:", error);
                alert("Failed to delete ad.");
            }
        }
    };

    if (loading) return <Spinner fullPage message="Loading Admin Dashboard..." />;

    return (
        <div className="admin-dashboard">
            <Helmet>
                <title>Admin Dashboard | RiyaMaga</title>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            <header className="admin-header">
                <div className="admin-header-left">
                    <h1>Admin Dashboard</h1>
                    <button className="btn-refresh" onClick={fetchAds} title="Refresh Data">
                        <i className="fa-solid fa-rotate"></i>
                    </button>
                </div>
                <Link to="/post-ad" className="btn-primary">
                    <i className="fa-solid fa-plus"></i> Create New Ad
                </Link>
            </header>

            <div className="admin-filters">
                <button
                    className={`admin-filter-btn ${filter === 'pending' ? 'active' : ''}`}
                    onClick={() => setFilter('pending')}
                >
                    Pending Approvals
                </button>
                <button
                    className={`admin-filter-btn ${filter === 'approved' ? 'active' : ''}`}
                    onClick={() => setFilter('approved')}
                >
                    Approved Ads
                </button>
                <button
                    className={`admin-filter-btn ${filter === 'all' ? 'active' : ''}`}
                    onClick={() => setFilter('all')}
                >
                    All Ads
                </button>
            </div>

            <div className="admin-table-container">
                {ads.length === 0 ? (
                    <div className="admin-empty">No ads found for the selected filter.</div>
                ) : (
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Image</th>
                                <th>Details</th>
                                <th>User</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ads.map(ad => (
                                <tr key={ad.id}>
                                    <td>
                                        <img src={ad.image || 'https://placehold.co/100'} alt={ad.title} className="admin-ad-img" />
                                    </td>
                                    <td>
                                        <strong>{ad.title}</strong>
                                        <div className="admin-ad-meta">
                                            <span>{ad.category}</span> | <span>{ad.price}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <div>{ad.userName || 'Unknown'}</div>
                                        <small>{ad.userEmail || ''}</small>
                                    </td>
                                    <td>
                                        <span className={`status-badge ${ad.status || 'approved'}`}>
                                            {ad.status || 'approved'}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="admin-actions">
                                            {ad.status === 'pending' && (
                                                <button className="btn-success" onClick={() => handleApprove(ad.id)}>
                                                    Approve
                                                </button>
                                            )}
                                            <button className="btn-danger" onClick={() => handleDelete(ad.id)}>
                                                Delete
                                            </button>
                                            <Link to={`/edit-ad/${ad.id}`} className="btn-edit">
                                                Edit
                                            </Link>
                                            <a href={`/ad/${ad.id}`} target="_blank" rel="noopener noreferrer" className="btn-view">
                                                View
                                            </a>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default AdminDashboard;
